/*
  File: src/pages/PlayersManagement.jsx
  Purpose: Manage players CRUD with ProductManagement-style UI and UX
*/
import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Loader from '../components/common/Loader';
import PlayersAPI from '../api/players';
import TeamsAPI from '../api/teams';

function ImagePicker({ value, onChange, error }) {
    const [preview, setPreview] = useState(value || '');
    const [localError, setLocalError] = useState('');
    useEffect(() => { setPreview(value || ''); }, [value]);

    const onFile = (file) => {
        if (!file) return;

        // Check file size (35KB = 35 * 1024 bytes)
        const maxSize = 35 * 1024;
        if (file.size > maxSize) {
            const msg = `Max size 35KB (current ${(file.size / 1024).toFixed(1)}KB)`;
            setLocalError(msg);
            setTimeout(() => setLocalError(''), 2500);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const dataUrl = e.target.result;
            setPreview(dataUrl);
            onChange({ file, dataUrl });
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="space-y-2">
            <div className={`border-2 border-dashed rounded-lg p-4 text-center ${(error || localError) ? 'border-red-400 bg-red-400/10' : 'border-white/20 hover:border-white/40'}`}>
                <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="player-image-input"
                    onChange={(e) => onFile(e.target.files[0])}
                />
                {preview ? (
                    <div className="space-y-2">
                        <img src={preview} alt="Preview" className="mx-auto max-h-28 rounded" />
                        <label htmlFor="player-image-input" className="inline-block px-3 py-1 rounded bg-white/10 border border-white/20 text-sm cursor-pointer">Change image</label>
                    </div>
                ) : (
                    <label htmlFor="player-image-input" className="block cursor-pointer">
                        <div className="space-y-2">
                            <svg className="mx-auto h-10 w-10 text-white/40" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <p className="text-white/70">Click to upload player image</p>
                            <p className="text-sm text-white/50">PNG, JPG (max 35KB)</p>
                        </div>
                    </label>
                )}
            </div>
            {(error || localError) && <p className="text-red-400 text-sm">{localError || error}</p>}
        </div>
    );
}

// Searchable single-select styled like ProductManagement SearchSelect
function TeamSearchSelect({ options, value, onChange, placeholder = 'Select team' }) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const safeOptions = Array.isArray(options) ? options : [];
    const selectedOption = useMemo(() => safeOptions.find(opt => String(opt.id) === String(value)) || null, [safeOptions, value]);

    const filteredOptions = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return safeOptions;
        return safeOptions.filter(opt =>
            String(opt.name || '').toLowerCase().includes(term) ||
            String(opt.display_name || '').toLowerCase().includes(term)
        );
    }, [safeOptions, searchTerm]);

    return (
        <div className="relative">
            <div
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 cursor-pointer min-h-[42px] flex items-center justify-between"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="truncate text-white text-xs sm:text-sm">
                    {selectedOption ? (selectedOption.display_name || selectedOption.name) : <span className="text-white/50">{placeholder}</span>}
                </div>
                <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-lg">
                    <div className="p-2 border-b border-white/10">
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-xs sm:text-sm text-white"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                        {filteredOptions.map(opt => (
                            <div
                                key={opt.id}
                                className={`px-3 py-2 cursor-pointer hover:bg-white/10 ${String(opt.id) === String(value) ? 'bg-cyan-600/20 text-cyan-300' : ''}`}
                                onClick={() => { onChange(opt.id); setIsOpen(false); setSearchTerm(''); }}
                            >
                                {opt.display_name || opt.name}
                            </div>
                        ))}
                        {filteredOptions.length === 0 && (
                            <div className="px-3 py-2 text-xs sm:text-sm text-white/60">No options available</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Predefined position select with the same styled searchable UI as TeamSearchSelect
function PositionSelect({ value, onChange, placeholder = 'Select position' }) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const options = [
        { id: 'Goalkeeper', label: 'Goalkeeper' },
        { id: 'Defender', label: 'Defender' },
        { id: 'Midfielder', label: 'Midfielder' },
        { id: 'Forward', label: 'Forward' },
    ];

    const selectedOption = useMemo(() => options.find(opt => String(opt.id) === String(value)) || null, [value]);
    const filteredOptions = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return options;
        return options.filter(opt => opt.label.toLowerCase().includes(term));
    }, [searchTerm]);

    return (
        <div className="relative">
            <div
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 cursor-pointer min-h-[42px] flex items-center justify-between"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="truncate text-white text-xs sm:text-sm">
                    {selectedOption ? selectedOption.label : <span className="text-white/50">{placeholder}</span>}
                </div>
                <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-lg">
                    <div className="p-2 border-b border-white/10">
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-xs sm:text-sm text-white"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                        {filteredOptions.map(opt => (
                            <div
                                key={opt.id}
                                className={`px-3 py-2 cursor-pointer hover:bg-white/10 ${String(opt.id) === String(value) ? 'bg-cyan-600/20 text-cyan-300' : ''}`}
                                onClick={() => { onChange(opt.id); setIsOpen(false); setSearchTerm(''); }}
                            >
                                {opt.label}
                            </div>
                        ))}
                        {filteredOptions.length === 0 && (
                            <div className="px-3 py-2 text-xs sm:text-sm text-white/60">No options available</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function RowsSelect({ value, onChange, options = [10, 20, 50, 100] }) {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="relative min-w-[86px]">
            <div
                className="w-full px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 cursor-pointer min-h-[34px] flex items-center justify-between text-xs sm:text-sm"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="truncate text-white/90">{value}</div>
                <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-lg">
                    <div className="max-h-60 overflow-y-auto">
                        {options.map(opt => (
                            <div
                                key={opt}
                                className={`px-3 py-2 cursor-pointer hover:bg-white/10 ${Number(opt) === Number(value) ? 'bg-cyan-600/20 text-cyan-300' : ''}`}
                                onClick={() => { onChange(Number(opt)); setIsOpen(false); }}
                            >
                                {opt}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function PlayersManagement() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [limit, setLimit] = useState(20);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null); // item or null
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [form, setForm] = useState({
        names: [''],
        display_name: '',
        team_id: '',
        position: '',
        image_url: ''
    });
    const [imageData, setImageData] = useState(null); // { file, dataUrl }
    const [teamsOptions, setTeamsOptions] = useState([]);

    const [search, setSearch] = useState('');
    const page = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit]);
    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await PlayersAPI.list({ limit, offset });
            setItems(Array.isArray(data.items) ? data.items : []);
            setTotal(Number(data.total || 0));
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to load players');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(); // initial
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit, offset]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await TeamsAPI.list({ limit: 100, offset: 0 });
                if (!cancelled) setTeamsOptions(Array.isArray(data.items) ? data.items : []);
            } catch (_) {
                if (!cancelled) setTeamsOptions([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const resetForm = () => {
        setForm({
            names: [''],
            display_name: '',
            team_id: '',
            position: '',
            image_url: ''
        });
        setImageData(null);
    };

    const openCreate = () => { resetForm(); setCreating(true); };
    const openEdit = (it) => {
        setEditing(it);
        setForm({
            names: Array.isArray(it.names) ? it.names : [it.display_name || '', ''],
            display_name: it.display_name || '',
            team_id: it.team_id || '',
            position: it.position || '',
            image_url: it.image_url || ''
        });
        setImageData(null);
    };

    const updateName = (index, value) => {
        const newNames = [...form.names];
        newNames[index] = value;
        setForm(prev => ({ ...prev, names: newNames }));
    };

    const submitForm = async (e) => {
        e?.preventDefault?.();
        const payload = {
            names: form.names.filter(name => name.trim() !== ''),
            display_name: form.display_name || undefined,
            team_id: form.team_id ? Number(form.team_id) : undefined,
            position: form.position || undefined,
        };
        if (form.image_url) payload.image_url = form.image_url;
        if (!form.image_url && imageData?.dataUrl) payload.image = imageData.dataUrl;

        try {
            if (editing) {
                const updated = await PlayersAPI.update(editing.id, payload, { useMultipart: Boolean(payload.image) });
                setItems((arr) => arr.map((x) => (x.id === editing.id ? updated : x)));
                setEditing(null);
            } else {
                const created = await PlayersAPI.create(payload, { useMultipart: Boolean(payload.image) });
                setItems((arr) => [created, ...arr]);
                setTotal((t) => t + 1);
                setCreating(false);
            }
            resetForm();
        } catch (e) {
            alert(e.response?.data?.message || e.message || 'Failed to save');
        }
    };

    const doDelete = async (it) => {
        try {
            await PlayersAPI.remove(it.id);
            setItems((arr) => arr.filter((x) => x.id !== it.id));
            setTotal((t) => Math.max(0, t - 1));
            setConfirmDelete(null);
        } catch (e) {
            alert(e.response?.data?.message || e.message || 'Failed to delete');
        }
    };

    return (
        <div className="min-h-screen flex flex-col pt-14 sm:pt-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
            <Navbar />
            <main className="flex-1 w-full max-w-5xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-2 w-full">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold">Players Management</h1>
                        <p className="text-white/70 text-xs sm:text-sm">Create, edit and delete players</p>
                    </div>
                    <button type="button" onClick={openCreate} className="px-4 py-2 text-sm sm:text-base bg-cyan-600 hover:bg-cyan-500 rounded-lg">New Player</button>
                </div>

                <div className="mb-4 flex flex-col sm:flex-row items-stretch gap-2 sm:gap-3 w-full">
                    <input
                        type="text"
                        placeholder="Search name, display name, team, or position"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full px-3 py-2 rounded bg-white/5 border border-white/10 text-sm"
                    />
                    <div className="flex items-center gap-2 justify-end">
                        <span className="hidden sm:inline text-white/60 text-xs sm:text-sm">Rows</span>
                        <RowsSelect value={limit} onChange={(v) => { setOffset(0); setLimit(v); }} />
                    </div>
                </div>

                <div className="bg-white/5 backdrop-blur rounded-xl border border-white/10 overflow-x-auto">
                    {loading ? (
                        <div className="py-16"><Loader label="Loading players..." /></div>
                    ) : error ? (
                        <div className="p-4 text-red-300">{error}</div>
                    ) : (
                        <div className="min-w-full">
                            <table className="min-w-[700px] w-full text-xs sm:text-sm">
                                <thead className="text-white/70">
                                    <tr>
                                        <th className="text-left px-2 sm:px-4 py-2 sm:py-3 min-w-[56px]">ID</th>
                                        <th className="text-left px-2 sm:px-4 py-2 sm:py-3 min-w-[72px]">Image</th>
                                        <th className="text-left px-2 sm:px-4 py-2 sm:py-3 min-w-[140px]">Names</th>
                                        <th className="text-left px-2 sm:px-4 py-2 sm:py-3 min-w-[140px]">Display Name</th>
                                        <th className="text-left px-2 sm:px-4 py-2 sm:py-3 min-w-[120px]">Team</th>
                                        <th className="text-left px-2 sm:px-4 py-2 sm:py-3 min-w-[100px]">Position</th>
                                        <th className="text-right px-2 sm:px-4 py-2 sm:py-3 min-w-[110px]">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items
                                        .filter((it) => {
                                            const q = search.trim().toLowerCase();
                                            if (!q) return true;
                                            return (
                                                String(it.display_name || '').toLowerCase().includes(q) ||
                                                (Array.isArray(it.names) && it.names.some(name => String(name || '').toLowerCase().includes(q))) ||
                                                String(it.team || '').toLowerCase().includes(q) ||
                                                String(it.position || '').toLowerCase().includes(q)
                                            );
                                        })
                                        .map((it) => (
                                            <tr key={it.id} className="border-t border-white/10">
                                                <td className="px-2 sm:px-4 py-2 sm:py-3 align-middle">{it.id}</td>
                                                <td className="px-2 sm:px-4 py-2 sm:py-3">
                                                    {it.image_url ? (
                                                        <img src={it.image_url} alt={it.display_name || it.names?.[0]} className="w-10 h-10 sm:w-12 sm:h-12 rounded object-cover mx-auto" />
                                                    ) : (
                                                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded bg-white/10 mx-auto" />
                                                    )}
                                                </td>
                                                <td className="px-2 sm:px-4 py-2 sm:py-3 align-middle">
                                                    <div className="space-y-1">
                                                        {Array.isArray(it.names) && it.names.length > 0 ? (
                                                            it.names.map((name, idx) => (
                                                                <div key={idx} className="text-xs">{name}</div>
                                                            ))
                                                        ) : (
                                                            <div className="text-xs text-white/50">—</div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-2 sm:px-4 py-2 sm:py-3 align-middle">{it.display_name || '—'}</td>
                                                <td className="px-2 sm:px-4 py-2 sm:py-3 align-middle">
                                                    <div className="space-y-1">
                                                        <div className="text-xs">{it.display_team_name || it.team || '—'}</div>
                                                    </div>
                                                </td>
                                                <td className="px-2 sm:px-4 py-2 sm:py-3 align-middle">{it.position || '—'}</td>
                                                <td className="px-2 sm:px-4 py-2 sm:py-3 flex flex-row justify-end gap-1 sm:gap-2 whitespace-nowrap">
                                                    <button onClick={() => openEdit(it)} className="px-2 sm:px-3 py-1 rounded border border-white/15 bg-white/5 hover:bg-white/10 text-xs sm:text-sm">Edit</button>
                                                    <button onClick={() => setConfirmDelete(it)} className="px-2 sm:px-3 py-1 rounded border border-red-400/40 bg-red-600/15 hover:bg-red-600/25 text-red-200 text-xs sm:text-sm">Delete</button>
                                                </td>
                                            </tr>
                                        ))}
                                    {items.length === 0 && (
                                        <tr><td colSpan={7} className="px-2 sm:px-4 py-8 sm:py-10 text-center text-white/60">No players</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="mt-4 flex flex-col sm:flex-row items-center justify-between text-xs sm:text-sm gap-2 w-full">
                    <div className="text-white/60">Total: {total}</div>
                    <div className="flex items-center gap-2">
                        <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} className="px-3 py-1 rounded bg-white/5 border border-white/10 disabled:opacity-50">Prev</button>
                        <div className="text-white/70">Page {page} / {totalPages}</div>
                        <button disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} className="px-3 py-1 rounded bg-white/5 border border-white/10 disabled:opacity-50">Next</button>
                    </div>
                </div>
            </main>
            <Footer />

            {(creating || editing) && (
                <div className="fixed inset-0 z-[2147483646] flex items-center justify-center px-2 sm:px-0">
                    <div className="absolute inset-0 bg-black/60" onClick={() => { setCreating(false); setEditing(null); }} />
                    <div className="relative w-full sm:w-[92vw] max-w-md sm:max-w-2xl rounded-2xl border border-white/10 bg-gradient-to-br from-[#0a1628]/95 to-[#1a2448]/95 shadow-2xl p-3 sm:p-5 mx-auto">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-base sm:text-lg font-semibold">{editing ? 'Edit Player' : 'New Player'}</div>
                            <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="text-white/70 hover:text-white">✕</button>
                        </div>
                        <form onSubmit={submitForm} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div className="sm:col-span-2">
                                    <label className="block text-xs sm:text-sm mb-1">Names</label>
                                    <div className="space-y-2">
                                        {form.names.map((name, index) => (
                                            <div key={index} className="flex gap-2">
                                            <input
                                                type="text"
                                                value={name}
                                                onChange={(e) => updateName(index, e.target.value)}
                                                placeholder={`Name ${index + 1}`}
                                                    className="flex-1 px-3 py-2 rounded bg-white/5 border border-white/10 text-xs sm:text-sm"
                                                />
                                                {form.names.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newNames = form.names.filter((_, i) => i !== index);
                                                            setForm(prev => ({ ...prev, names: newNames }));
                                                        }}
                                                        className="px-2 py-2 rounded bg-red-600/20 border border-red-400/40 hover:bg-red-600/30 text-red-200 text-xs"
                                                        title="Remove name"
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setForm(prev => ({ ...prev, names: [...prev.names, ''] }));
                                            }}
                                            className="w-full px-3 py-2 rounded bg-white/5 border border-white/20 hover:bg-white/10 text-xs sm:text-sm text-white/70 hover:text-white"
                                        >
                                            + Add another name
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs sm:text-sm mb-1">Display Name</label>
                                    <input type="text" value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} className="w-full px-3 py-2 rounded bg-white/5 border border-white/10 text-xs sm:text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs sm:text-sm mb-1">Team</label>
                                    <TeamSearchSelect
                                        options={teamsOptions}
                                        value={form.team_id}
                                        onChange={(v) => setForm((f) => ({ ...f, team_id: v }))}
                                        placeholder="Select team"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs sm:text-sm mb-1">Position</label>
                                    <PositionSelect
                                        value={form.position}
                                        onChange={(v) => setForm((f) => ({ ...f, position: v }))}
                                        placeholder="Select position"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs sm:text-sm mb-1">Image URL (optional)</label>
                                    <input type="url" value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="https://..." className="w-full px-3 py-2 rounded bg-white/5 border border-white/10 text-xs sm:text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs sm:text-sm mb-2">Upload Image (or provide URL)</label>
                                <ImagePicker
                                    value={editing?.image_url || ''}
                                    onChange={(img) => { setImageData(img); setForm((f) => ({ ...f, image_url: '' })); }}
                                />
                                <p className="text-[10px] sm:text-[11px] text-white/50 mt-1">If you upload an image, it will be stored and URL saved.</p>
                            </div>
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 w-full mt-2">
                                <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="px-4 py-2 rounded bg-white/5 border border-white/10 text-sm">Cancel</button>
                                <button type="submit" className="px-4 py-2 rounded bg-cyan-600 hover:bg-cyan-500 text-sm">{editing ? 'Save Changes' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {confirmDelete && (
                <div className="fixed inset-0 z-[2147483646] flex items-center justify-center px-2 sm:px-0">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setConfirmDelete(null)} />
                    <div className="relative w-full sm:w-[92vw] max-w-xs sm:max-w-sm rounded-2xl border border-white/10 bg-gradient-to-br from-[#1b1f3a]/95 to-[#222a55]/95 shadow-2xl p-3 sm:p-5 mx-auto">
                        <div className="text-base sm:text-lg font-semibold mb-2">Delete Player</div>
                        <p className="text-white/80 text-xs sm:text-sm mb-4">Are you sure you want to delete "{confirmDelete.display_name || confirmDelete.names?.[0]}"?</p>
                        <div className="flex flex-col sm:flex-row justify-end gap-2 w-full">
                            <button type="button" onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded bg-white/5 border border-white/10 text-sm">Cancel</button>
                            <button type="button" onClick={() => doDelete(confirmDelete)} className="px-4 py-2 rounded bg-red-600 hover:bg-red-500 text-sm">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

